import { Component, computed, effect, ElementRef, HostListener, inject, signal, viewChild } from '@angular/core';
import { UniversalSearchService } from 'src/app/core/services/search/universal-search.service';
import { isRemoteSearchQuery, LOCAL_SEARCH_MIN_QUERY_LENGTH, REMOTE_SEARCH_MIN_QUERY_LENGTH, UNIVERSAL_SEARCH_KIND_LABELS, UniversalSearchKind, UniversalSearchResult } from 'src/app/core/services/search/universal-search.models';
import { UniversalSearchIconComponent } from './universal-search-icon.component';

interface IndexedResult {
  result: UniversalSearchResult;
  index: number;
}

/**
 * Command-palette overlay for Universal Search. Mounted once at the application root and opened
 * with the main menu button or Cmd/Ctrl+K.
 */
@Component({
  selector: 'universal-search-overlay',
  standalone: true,
  imports: [UniversalSearchIconComponent],
  templateUrl: './universal-search-overlay.component.html',
})
export class UniversalSearchOverlayComponent {
  public universalSearchService = inject(UniversalSearchService);

  public readonly activeIndex = signal(0);

  private searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');

  public readonly minQueryLength = computed(() => (this.universalSearchService.hasActiveInspection() ? LOCAL_SEARCH_MIN_QUERY_LENGTH : REMOTE_SEARCH_MIN_QUERY_LENGTH));
  public readonly hasQuery = computed(() => {
    const query = this.universalSearchService.query().trim();
    return this.universalSearchService.hasActiveInspection() ? query.length >= LOCAL_SEARCH_MIN_QUERY_LENGTH : isRemoteSearchQuery(query);
  });
  public readonly minimumQueryHint = computed(() =>
    this.universalSearchService.hasActiveInspection()
      ? `Type at least ${LOCAL_SEARCH_MIN_QUERY_LENGTH} characters to search the open inspection. Workspace results appear after ${REMOTE_SEARCH_MIN_QUERY_LENGTH} characters.`
      : `Type at least ${REMOTE_SEARCH_MIN_QUERY_LENGTH} characters to search this workspace.`
  );

  public readonly indexedGroups = computed(() => {
    let index = 0;
    return this.universalSearchService.groups().map((group) => ({
      kind: group.kind,
      label: group.label,
      results: group.results.map((result): IndexedResult => ({ result, index: index++ })),
    }));
  });

  constructor() {
    effect(
      () => {
        if (this.universalSearchService.isOpen()) {
          setTimeout(() => this.searchInput()?.nativeElement.focus());
        }
      },
      { allowSignalWrites: false }
    );

    effect(
      () => {
        const resultCount = this.universalSearchService.results().length;
        if (this.activeIndex() > Math.max(resultCount - 1, 0)) {
          this.activeIndex.set(0);
        }
      },
      { allowSignalWrites: true }
    );
  }

  @HostListener('document:keydown', ['$event'])
  public handleKeydown(event: KeyboardEvent): void {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k' && this.universalSearchService.isAvailable()) {
      event.preventDefault();
      this.universalSearchService.toggle();
      return;
    }

    if (!this.universalSearchService.isOpen()) return;

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        this.universalSearchService.close();
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.moveActiveIndex(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.moveActiveIndex(-1);
        break;
      case 'Enter': {
        const result = this.universalSearchService.results()[this.activeIndex()];
        if (result) {
          event.preventDefault();
          void this.universalSearchService.select(result);
        }
        break;
      }
    }
  }

  public onInput(event: Event): void {
    this.universalSearchService.setQuery((event.target as HTMLInputElement).value);
    this.activeIndex.set(0);
  }

  public kindLabel(kind: UniversalSearchKind): string {
    return UNIVERSAL_SEARCH_KIND_LABELS[kind];
  }

  private moveActiveIndex(step: number): void {
    const results = this.universalSearchService.results();
    if (results.length === 0) return;

    const nextIndex = (this.activeIndex() + step + results.length) % results.length;
    this.activeIndex.set(nextIndex);
    document.getElementById(`universalSearchResult${nextIndex}`)?.scrollIntoView({ block: 'nearest' });
  }
}
